// =============================================================================
// LP Express — "Cold Drip Offer (Gaffle)" template content model.
//
// Imported from the Gaffle landing-template export's `cold-drip` template
// (offer page for the Cold Drip Bundle). Like the Quiz/Listicle/Advertorial
// templates, the ENTIRE page copy lives as ONE structured blob at
// `generatedContent.coldDrip` (alongside `templateType: "cold-drip"`), edited
// in place via the shared path-based `setText`/`setLinkUrl` ops (paths like
// ["coldDrip","hero","headline"]).
//
// Unlike the other blob templates, generation does NOT call a Sim workflow:
// the export's copy is embedded verbatim below as the template's default
// content, so a new run persists it synchronously and every field is edited
// manually. Per-section copy regeneration is wired through a dedicated route
// (dynamic block keys `cold-drip-<sectionKey>`, mirroring the advertorial's
// `advertorial-section-<n>` precedent) that calls a Sim workflow at
// SIM_LP_COLD_DRIP_URL — a placeholder until that workflow is built, so the
// control fails with a clear "not configured" error rather than guessing.
//
// Imagery: the export's asset binaries were NOT included in the export file
// (filenames only), so every image surface is a media-picker/upload slot
// (generatedContent.mediaOverrides via the standard onChooseMedia /
// onUploadMedia mechanism) rendering a neutral placeholder until an asset is
// provided. Nothing hotlinks the export's Shopify CDN / TikTok URLs (not
// durable).
//
// Section reordering / hide / delete: each top-level section renders inside an
// EditableElement (element-state hide/delete) within ONE page-level
// LpReorderGroup (generatedContent.componentOrder), so order and visibility
// persist and are honored on the published page.
//
// This module is pure + dependency-free so it runs identically on the client
// (renderer + editor) and the server (generation / regen merge).
// =============================================================================

// ── Section catalog (from the export's sections.json, in order) ─────────────

export const LP_COLD_DRIP_SECTIONS: { key: string; label: string }[] = [
  { key: "promoBanner", label: "Promo Banner" },
  { key: "header", label: "Sticky Header" },
  { key: "hero", label: "Hero + Buy Box" },
  { key: "marquee", label: "Marquee Ticker" },
  { key: "meetTheDuo", label: "Meet the Duo" },
  { key: "bundleBenefits", label: "Bundle Benefits" },
  { key: "whyGaffle", label: "Why Gaffle" },
  { key: "gutProblem", label: "The Gut Problem" },
  { key: "comparison", label: "Us vs Them" },
  { key: "inlineBuyCta", label: "Inline Buy CTA" },
  { key: "ingredients", label: "Ingredients" },
  { key: "gaffleWorld", label: "Gaffle World" },
  { key: "joinTheFam", label: "Join the Fam CTA" },
  { key: "reviews", label: "Reviews" },
  { key: "whoWeAre", label: "Who We Are" },
  { key: "visit", label: "Visit the Pour Bar" },
  { key: "ctaStrip", label: "CTA Strip" },
  { key: "faq", label: "FAQ" },
  { key: "footer", label: "Footer" },
  { key: "stickyBar", label: "Sticky Bottom Bar" },
];

export const LP_COLD_DRIP_SECTION_KEYS = LP_COLD_DRIP_SECTIONS.map((s) => s.key);

export function lpColdDripSectionLabel(key: string): string | undefined {
  return LP_COLD_DRIP_SECTIONS.find((s) => s.key === key)?.label;
}

// ── Content types ────────────────────────────────────────────────────────────

export interface LpColdDripProduct {
  caffeine: string;
  title: string;
  kicker: string;
  tasting: string;
  blurb: string;
  reviews: string;
  bottomNote: string;
  bottomNoteEmphasis: string;
  properties: { label: string; value: string }[];
}

export interface LpColdDripContent {
  pricing: {
    bundlePriceOneTime: number;
    bundleFullPrice: number;
    checkoutUrl: string;
  };
  promoBanner: string;
  header: { shopPill: string; signIn: string; bag: string };
  hero: {
    badge: string;
    headline: string;
    subheadline: string;
    offerPill: string;
    tagline: string;
    body: string;
    reviewStat: string;
    cupsStat: string;
    benefitChips: string[];
    guarantee: string;
  };
  buyBox: {
    badge: string;
    savePill: string;
    title: string;
    subtitle: string;
    savingsLine: string;
    ctaPrimary: string;
    ctaSecondary: string;
    trustBadges: string[];
  };
  marquee: string[];
  meetTheDuo: {
    eyebrow: string;
    heading: string;
    body: string;
    pill: string;
    products: LpColdDripProduct[];
    sharedCta: { eyebrow: string; heading: string; body: string };
  };
  bundleBenefits: {
    badge: string;
    heading: string;
    intro: string;
    cards: { tag: string; body: string }[];
    cta: string;
    ingredientLine: string;
  };
  whyGaffle: {
    eyebrow: string;
    heading: string;
    intro: string;
    benefits: { tag: string; title: string; body: string; sourceLabel: string; sourceUrl: string }[];
  };
  gutProblem: {
    eyebrow: string;
    heading: string;
    intro: string;
    stats: { stat: string; label: string }[];
    cards: { title: string; body: string; source: string; href: string }[];
    bridgeLine: string;
    cta: string;
  };
  comparison: {
    eyebrow: string;
    heading: string;
    columns: { us: string; them: string };
    rows: { us: string; them: string }[];
    footnote: string;
    cta: string;
    ctaSubtext: string;
  };
  inlineBuyCta: { eyebrow: string; heading: string; body: string; bullets: string[] };
  ingredients: {
    eyebrow: string;
    heading: string;
    intro: string;
    cards: { title: string; lead: string; bullets: string[] }[];
    cta: string;
    ctaSubtext: string;
  };
  gaffleWorld: {
    eyebrow: string;
    heading: string;
    intro: string;
    followCta: string;
    followUrl: string;
    tiktoks: { caption: string }[];
  };
  joinTheFam: { eyebrow: string; heading: string; body: string; chips: string[] };
  reviews: {
    eyebrow: string;
    heading: string;
    intro: string;
    ratingSummary: string;
    items: { name: string; title: string; body: string; badge: string }[];
  };
  whoWeAre: {
    eyebrow: string;
    heading: string;
    intro: string;
    behindTheDrip: { title: string; body1: string; body2: string; cta: string; ctaUrl: string };
    skincare: { eyebrow: string; title: string; body1: string; body2: string; cta: string; ctaUrl: string };
  };
  visit: {
    badge: string;
    heading: string;
    intro: string;
    stats: { num: string; label: string }[];
    doorSign: {
      header: string;
      openBanner: string;
      hoursLabel: string;
      hours: { day: string; hours: string }[];
      addressCallout: string;
      address: string;
      mapsCta: string;
      mapsUrl: string;
    };
    contacts: {
      phoneLabel: string;
      phone: string;
      phoneHref: string;
      emailLabel: string;
      email: string;
      emailHref: string;
    };
  };
  ctaStrip: { heading: string; body: string; cta: string };
  faq: { eyebrow: string; heading: string; items: { q: string; a: string }[] };
  footer: {
    tagline: string;
    links: { label: string; href: string }[];
    copyright: string;
    signoff: string;
  };
  stickyBar: { label: string; cta: string };
}

// ── Default content (VERBATIM from the export's content.json) ───────────────

export const LP_COLD_DRIP_DEFAULT_CONTENT: LpColdDripContent = {
  pricing: {
    bundlePriceOneTime: 39.92,
    bundleFullPrice: 49.9,
    checkoutUrl: "https://gaffle.coffee/products/cold-drip",
  },
  promoBanner: "THE GAFFLE IS REAL: 20% OFF THE BUNDLE",
  header: { shopPill: "Shop", signIn: "SIGN IN", bag: "BAG" },
  hero: {
    badge: "✦ Bundle Deal · 2 Bottles, One Price",
    headline: "The Cold Drip Bundle.",
    subheadline: "2 Flavors · 32 Cups · No Jitters",
    offerPill: "20% Off · Free U.S. Shipping",
    tagline: "Stay shaking.",
    body: "You should expect more from your cold coffee. We slow-drip our signature blend for 12 hours, never heat-touch it, and bottle it cold for a smoother, lower-acid, jitter-free cup.",
    reviewStat: "2,487 Verified Reviews",
    cupsStat: "1.2M+ Cold Cups Poured",
    benefitChips: ["67% less acid", "12+ hour cold drip", "Zero sweeteners", "Small-batch, USA"],
    guarantee: "60 DAY MONEY BACK GUARANTEE",
  },
  buyBox: {
    badge: "Bundle · 2 bottles",
    savePill: "Save up to 20%",
    title: "The Cold Drip Bundle",
    subtitle: "2 × 32 oz · The Classic + Blue Magic · 16 cold cups",
    savingsLine: "You Save {savings} Today · {percent}% Off",
    ctaPrimary: "Try It Now · 20% Off",
    ctaSecondary: "Add to bag",
    trustBadges: ["Free U.S. shipping", "60-day guarantee"],
  },
  marquee: [
    "❄️ LOWER ACIDITY",
    "💎 SMALL-BATCH",
    "🍾 STAY SHAKING",
    "⚡ CLEAN ENERGY",
    "👋 REAL COFFEE",
    "🌱 ZERO PRESERVATIVES",
    "🇺🇸 MADE IN AMERICA",
  ],
  meetTheDuo: {
    eyebrow: "Inside the bundle",
    heading: "Meet the duo.",
    body: "No need to choose, the bundle includes both. Here's what's inside: two takes on our signature blend, slow-dripped 12 hours and bottled cold. Same process, two very different cups.",
    pill: "✓ Both Bottles · One Bundle Price",
    products: [
      {
        caffeine: "80mg Caffeine",
        title: "The Classic Cold Drip",
        kicker: "Smooth, balanced, and deliberately crafted.",
        tasting: "smooth · balanced · clean",
        blurb:
          "The foundation of every Gaffle ritual. Our signature blend delivers natural sweetness, balanced complexity, and lower acidity than traditional cold brew.",
        reviews: "+2,400 Reviews",
        bottomNote: "No fillers. No preservatives. Made in America.",
        bottomNoteEmphasis: "How it was always meant to be.",
        properties: [
          { label: "Origin", value: "Signature blend" },
          { label: "Flavor profile", value: "Dark chocolate · Caramel · Cherry" },
          { label: "Size", value: "32oz · 16 servings" },
          { label: "Caffeine", value: "Caffeinated · Slow-dripped 12 hours" },
        ],
      },
      {
        caffeine: "90mg Caffeine",
        title: "Blue Magic Cold Drip",
        kicker: "Funkier, bolder, more expressive.",
        tasting: "bold · expressive · smooth",
        blurb:
          "Bold, expressive, and unforgettable. Orange, vanilla, and milk chocolate dance together in perfect harmony. A showstopper concentrate that transforms your ritual.",
        reviews: "+1,100 Reviews",
        bottomNote: "No fillers. No preservatives. Nothing unnatural. Made in America.",
        bottomNoteEmphasis: "How it was always meant to be.",
        properties: [
          { label: "Origin", value: "Signature blend" },
          { label: "Flavor profile", value: "Orange zest · Vanilla · Milk chocolate" },
          { label: "Size", value: "32oz · 16 servings" },
          { label: "Caffeine", value: "Caffeinated · Slow-dripped 12 hours" },
        ],
      },
    ],
    sharedCta: {
      eyebrow: "Try both",
      heading: "Get the Cold Drip Bundle.",
      body: "One bottle of each, The Classic and Blue Magic. 16 cold cups. Save 20% versus single bottles, plus free U.S. shipping.",
    },
  },
  bundleBenefits: {
    badge: "Bundle Exclusive · Save vs Single Bottles",
    heading: "Cold Drip Bundle Benefits",
    intro: "The all-in-one cold coffee bundle that makes you feel as good as it tastes.",
    cards: [
      {
        tag: "TASTE",
        body: "Balanced, full-bodied cold coffee you know and love. Classic brings notes of dark chocolate, caramel, and cherry. Blue Magic layers in orange zest, vanilla, and milk chocolate.",
      },
      {
        tag: "FEELING",
        body: "Calm, focused, and caffeinated. The way coffee is supposed to be, without the jitters or stomach issues. No crash, no problems.",
      },
    ],
    cta: "Try It Now · 20% Off",
    ingredientLine:
      "Full ingredient list: Signature blend coffee, California spring water. Other ingredients: None.",
  },
  whyGaffle: {
    eyebrow: "The Gaffle Difference",
    heading: "Cold coffee, done right.",
    intro:
      "Cold-water dripped over our fresh-ground signature blend for 12+ hours. Bottled the same day, always shipped cold.",
    benefits: [
      {
        tag: "LESS ACID",
        title: "Easier on Your Stomach",
        body: "Cold-water extraction pulls fewer of the bitter, acidic compounds that hot brewing releases. Peer-reviewed chemistry shows cold brew carries a measurably different acidity profile than hot coffee, meaning less heartburn, less stomach irritation, and a smoother sip from the first taste to the last.",
        sourceLabel: "Rao & Fuller, Scientific Reports (2018)",
        sourceUrl: "https://www.nature.com/articles/s41598-018-34392-w",
      },
      {
        tag: "12+ HOUR SLOW DRIP",
        title: "Smoother, Never Bitter",
        body: "Most bitterness in coffee is created by heat, chlorogenic-acid lactones and harsh phenolics released only at brewing temperatures. Skip the heat and you skip most of the bitter. Our 12-hour cold drip pulls chocolate, nut, and caramel notes forward while leaving the sharp edge behind, confirmed by side-by-side studies of hot vs cold brew chemistry.",
        sourceLabel: "Rao, Fuller & Grim, Foods (2020)",
        sourceUrl: "https://www.mdpi.com/2304-8158/9/7/902",
      },
      {
        tag: "ANTIOXIDANTS INTACT",
        title: "More of the Good Stuff",
        body: "Cold brew preserves chlorogenic acids and other coffee polyphenols, the antioxidants tied to healthier blood-sugar response, lower inflammation, and long-term metabolic benefits across dozens of human trials. Heat degrades these compounds over time; cold extraction keeps them intact and bioavailable in every bottle.",
        sourceLabel: "Cornelis, Nutrients (2019)",
        sourceUrl: "https://www.mdpi.com/2072-6643/11/2/416",
      },
      {
        tag: "STAY SHAKING",
        title: "Cleaner, Steadier Energy",
        body: "Cold-extracted caffeine arrives with a different chemical matrix than hot coffee, and paired with naturally occurring L-theanine (especially in our Blue Magic blend), it produces a steadier focus curve with less of the spike-and-crash hot brew is known for. Sustained alertness, no jittery peak, no afternoon collapse.",
        sourceLabel: "Owen et al., Nutritional Neuroscience (2008)",
        sourceUrl: "https://pubmed.ncbi.nlm.nih.gov/18681988/",
      },
    ],
  },
  gutProblem: {
    eyebrow: "The Gut Problem",
    heading: "Acidic coffee is wrecking your gut.",
    intro:
      "Most coffee is loaded with acid, chlorogenic acid, quinic acid, and harsh phenolics that sit in your stomach long after the last sip. The result: reflux, irritation, jitters, and a microbiome that pays the price every morning.",
    stats: [
      {
        stat: "60M+",
        label:
          "Americans suffer from reflux at least once a month, acidic coffee is one of the top dietary triggers.",
      },
      {
        stat: "5.48",
        label:
          "Average pH of standard coffee. Anything below 7 is acidic, coffee sits closer to tomato juice than water.",
      },
      {
        stat: "67%",
        label:
          "Of regular coffee drinkers report heartburn, indigestion, or stomach pain after their morning cup.",
      },
    ],
    cards: [
      {
        title: "Acid Reflux & Heartburn",
        body: "Coffee acid relaxes the lower esophageal sphincter, the valve that keeps stomach acid where it belongs. The more acidic the brew, the more often that valve leaks. Gastroenterologists routinely tell GERD patients to cut acidic coffee first.",
        source: "American College of Gastroenterology, GERD guidelines",
        href: "https://gi.org/topics/acid-reflux/",
      },
      {
        title: "Gastritis & Stomach Lining Irritation",
        body: "High-acid coffee is packed with chlorogenic and quinic acids, which directly stimulate gastric acid secretion. Over time that aggravates gastritis, ulcers, and the constant low-grade burn so many daily drinkers learn to ignore.",
        source: "Critical Reviews in Food Science & Nutrition (2017)",
        href: "https://pubmed.ncbi.nlm.nih.gov/26167726/",
      },
      {
        title: "Cortisol Spike & Crash",
        body: "Acidic coffee delivers caffeine in a sharp pulse that spikes cortisol on top of the stress response your gut is already managing. The result: a jittery peak, an anxious stomach, and the 2 p.m. crash that sends you back for another acidic cup.",
        source: "Psychosomatic Medicine, caffeine & cortisol",
        href: "https://pubmed.ncbi.nlm.nih.gov/16177242/",
      },
    ],
    bridgeLine:
      "Cut the acid, keep the ritual. Cold drip pulls the flavor forward and leaves the burn behind.",
    cta: "Try The Gentler Brew · 20% Off",
  },
  comparison: {
    eyebrow: "Why Gaffle Wins",
    heading: "Gaffle vs Store-Bought Cold Brew",
    columns: { us: "Gaffle", them: "STORE-BOUGHT COLD BREW" },
    rows: [
      { us: "Cold-drip extracted, slow & precise", them: "Cold brew (immersion steep, less precise)" },
      { us: "Lower natural acidity, gentle on gut", them: "Higher acidity from immersion extraction" },
      { us: "Clean, complex tasting notes", them: "Flat, generic coffee flavor" },
      { us: "Small-batch, craft production", them: "Mass-produced at industrial scale" },
      { us: "No added preservatives or stabilizers", them: "May contain stabilizers or additives" },
      { us: "Premium glass packaging", them: "Plastic packaging" },
      { us: "Naturally balanced caffeine", them: "Inconsistent caffeine levels" },
    ],
    footnote: "Not all coffee concentrates are created equal.",
    cta: "Try The Cold Drip Bundle · 20% Off",
    ctaSubtext: "Free U.S. Shipping · 60-Day Money-Back Guarantee",
  },
  inlineBuyCta: {
    eyebrow: "Ready when you are",
    heading: "Taste the difference for yourself",
    body: "16 cold cups across both flavors, Classic and Blue Magic. 20% off your first bundle, free U.S. shipping, and our 60-day money-back guarantee.",
    bullets: [
      "Slow-dripped 12 hours · Never heat-touched",
      "Signature blend + California water, only 2 ingredients",
      "Lower acidity, smoother finish, steadier energy",
    ],
  },
  ingredients: {
    eyebrow: "Honest sourcing",
    heading: "It's What's Inside That Counts",
    intro:
      "We're obsessed with quality. Every ingredient is sourced, brewed, and bottled to make a cold coffee that tastes amazing and helps you feel your best every day.",
    cards: [
      {
        title: "100% SIGNATURE BLEND",
        lead: "Coffee-forward taste, without the crash.",
        bullets: ["Signature blend, specialty grade", "Naturally lower caffeine spike", "Smoother, never burnt"],
      },
      {
        title: "CALIFORNIA WATER",
        lead: "Pure spring water, never tap.",
        bullets: ["Filtered, low mineral", "Lets coffee flavor lead", "Drawn fresh in Temecula"],
      },
    ],
    cta: "Try It Now · 20% Off",
    ctaSubtext: "60-Day Money-Back Guaranteed",
  },
  gaffleWorld: {
    eyebrow: "From the community",
    heading: "Gaffle World",
    intro: "Real fans, real pours, real moments. Tagged us @gaffle.coffee — we're tagging back.",
    followCta: "Follow @gaffle.coffee on TikTok",
    followUrl: "https://www.tiktok.com/@gaffle.coffee",
    tiktoks: [
      { caption: "If you're not shaking, you need another cup!" },
      { caption: "Milan started a coffee brand 👀😇" },
      { caption: "Cold drip tower in action, Dallas" },
      { caption: "Carina stays energized, clean caffeine" },
      { caption: "A little sunset refresher" },
      { caption: "Dreamy girls drink Gaffle" },
      { caption: "Gaffle Girl Carina 🎾" },
      { caption: "It's a Gaffle Christmas 🎁" },
    ],
  },
  joinTheFam: {
    eyebrow: "Stay shaking with us",
    heading: "Join the Gaffle Fam",
    body: "Thousands of cold-coffee drinkers swapped grocery-store cold brew for Gaffle and never looked back. Start with the bundle — if it's not the smoothest cold cup you've had in a year, we'll refund every penny.",
    chips: ["20% off your first bundle", "Free U.S. shipping", "60-day money-back guarantee"],
  },
  reviews: {
    eyebrow: "+2,400 five-star reviews",
    heading: "Join The Gaffle Fam",
    intro:
      "Don't just take our word for it. Hear from real people upgrading their cold coffee and getting more out of every day.",
    ratingSummary: "2,487 reviews",
    items: [
      {
        name: "Vanessa P.",
        title: "OBSESSED MOM OF 3!",
        body: "As a mom of 3 littles under 5 I need my coffee. I was feeling so guilty drinking conventional coffee and still crashing midday. This is not only delicious, it's guilt free, good for me, and I love how it makes me feel. I'm more focused and have sustainable energy throughout the day.",
        badge: "Verified Purchase",
      },
      {
        name: "Tamara F.",
        title: "I'M BECOMING A MORNING PERSON.",
        body: "I've never been a morning person and have never been able to get rid of the brain fog for at least 2 hours after waking up. But now I wake up cheerful, my husband wants to know where his wife is lol, and productive!",
        badge: "Verified Purchase",
      },
      {
        name: "Joci R.",
        title: "SO IN LOVE WITH THIS COFFEE!",
        body: "The taste is exactly like coffee but the results are so much better. I instantly feel energized, and over the last few weeks I can feel my sleep getting better and my gut health improving. I was taking separate supplements for those things, and now it is all in my morning coffee. Winning!!",
        badge: "Verified Purchase",
      },
    ],
  },
  whoWeAre: {
    eyebrow: "Who we are",
    heading: "Built For The Cold Cup",
    intro:
      "We're here to help you stay shaking through better cold coffee, slower brewing, and a community that loves a real cup as much as we do.",
    behindTheDrip: {
      title: "Behind The Drip",
      body1:
        "From Tokyo to Jeddah to the volcanic ridges of Colima, our founders chased one universal ritual: the connection forged over a truly great cup of coffee. That journey became Gaffle — the world's first dedicated cold coffee concept.",
      body2:
        "Family-owned, slow-dripped, and proudly never heat-touched. Every cup is an invitation to slow down, savor, and journey in taste.",
      cta: "Read Our Full Story",
      ctaUrl: "https://gaffle.coffee/pages/about-us",
    },
    skincare: {
      eyebrow: "02 / Sustainability in Skincare",
      title: "The Bean Doesn't Stop at the Cup",
      body1:
        "Spent coffee grounds — rich in antioxidants and caffeine — revitalize skin, improve circulation, and energize texture. We blend them with grass-fed tallow, raw honey, and cold-pressed olive oil for skincare that's both ancestral and innovative.",
      body2: "Sustainability with purpose — honoring the full life of the bean.",
      cta: "Shop Gaffle Skincare",
      ctaUrl: "https://gaffle.coffee/collections/skincare",
    },
  },
  visit: {
    badge: "VISIT US · TEMECULA, CA",
    heading: "This isn't a factory. It's a pour bar.",
    intro:
      "Real address. Real cold drip. Walk in, meet the crew, and try every flavor before you commit to a bottle.",
    stats: [
      { num: "12hr", label: "Cold drip per batch" },
      { num: "3", label: "Cups always pouring" },
      { num: "20%", label: "Lower acidity" },
    ],
    doorSign: {
      header: "Gaffle Pour Bar",
      openBanner: "Pouring Now",
      hoursLabel: "Hours of Operation",
      hours: [
        { day: "Monday", hours: "8 AM – 5 PM" },
        { day: "Tuesday", hours: "8 AM – 5 PM" },
        { day: "Wednesday", hours: "8 AM – 5 PM" },
        { day: "Thursday", hours: "8 AM – 5 PM" },
        { day: "Friday", hours: "8 AM – 5 PM" },
        { day: "Saturday", hours: "8 AM – 5 PM" },
        { day: "Sunday", hours: "Closed" },
      ],
      addressCallout: "We have a real address. Come pour with us.",
      address: "27725 Jefferson Ave STE 103, Temecula, CA 92590",
      mapsCta: "Tap to open in Maps",
      mapsUrl: "https://maps.google.com/?q=27725+Jefferson+Ave+STE+104+Temecula+CA+92590",
    },
    contacts: {
      phoneLabel: "Call ahead",
      phone: "(404) 490-1143",
      phoneHref: "tel:+14044901143",
      emailLabel: "Wholesale",
      email: "hello@gaffle.coffee",
      emailHref: "mailto:hello@gaffle.coffee",
    },
  },
  ctaStrip: {
    heading: "Stay shaking.",
    body: "One bottle of Cold Drip Concentrate replaces a week of $6 cold brews. Start your subscription and never run out.",
    cta: "Try It Now · 20% Off",
  },
  faq: {
    eyebrow: "Questions",
    heading: "Cold drip, answered.",
    items: [
      {
        q: "What is cold drip concentrate?",
        a: "Slow-dripped, never heat-extracted coffee. Cold water passes through fresh-ground beans for 12+ hours, pulling out the smooth, lower-acid flavors and skipping the bitter ones. You get a clean, ready-to-pour concentrate that lives in your fridge.",
      },
      {
        q: "How many cups per bottle?",
        a: "Each 32 oz bottle makes about 16 servings. Mix 1 oz of concentrate with cold water or milk over ice for a perfect cold cup. Want it stronger? Pour more.",
      },
      {
        q: "Why is the Cold Drip Bundle a better deal than buying single bottles?",
        a: "The bundle is the easiest way to try both Gaffle drops, Classic and Blue Magic, in one order, at the lowest per-bottle price we offer. It ships free in the U.S. as part of the Spring Drop and is backed by our 60-day money-back guarantee, so it's the simplest way to keep your fridge stocked.",
      },
      {
        q: "How do I store it?",
        a: "Keep it refrigerated. Sealed, it's good for 90 days. Once opened, finish within 30 days for peak flavor.",
      },
      {
        q: "Is it sweetened?",
        a: "No. Zero sweeteners, zero preservatives, zero filler. Just our slow-brewed signature blend. Add what you like.",
      },
      {
        q: "How is shipping?",
        a: "Free U.S. shipping is included with the Cold Drip Bundle and the Starter Kit during the Spring Drop. Bottles ship cold-pack from California within 1 to 2 business days.",
      },
      {
        q: "Can I cancel a subscription?",
        a: "Just contact us to cancel your subscription!",
      },
    ],
  },
  footer: {
    tagline: "Specialty cold coffee + caffeine skincare, made small-batch in Temecula, California.",
    links: [
      { label: "Shop", href: "https://gaffle.coffee/" },
      { label: "FAQ", href: "https://gaffle.coffee/pages/faq" },
      { label: "Contact Us", href: "https://gaffle.coffee/policies/contact-information" },
    ],
    copyright: "© {year} Gaffle LLC. All rights reserved.",
    signoff: "Stay shaking.",
  },
  stickyBar: { label: "· 20% OFF Cold Drip Bundle", cta: "Try It Now · 20% Off" },
};

// ── Normalization ────────────────────────────────────────────────────────────
// Generic type-preserving overlay: raw values are accepted only when they match
// the default's type at the same position (strings for strings, arrays of
// same-shaped items, nested objects recursed). Anything missing or mistyped
// falls back to the default, so the renderer can never crash on a partial or
// drifted payload (e.g. from a future Sim workflow, or an older stored blob
// after the model gains a field). NOTE: any new editable field must be added
// to LpColdDripContent AND LP_COLD_DRIP_DEFAULT_CONTENT or it will be dropped
// here (same rule as the quiz's normalize).

function overlay(def: any, raw: any): any {
  if (typeof def === "string") return typeof raw === "string" ? raw : def;
  if (typeof def === "number") {
    const n = typeof raw === "number" ? raw : Number(raw);
    return Number.isFinite(n) ? n : def;
  }
  if (typeof def === "boolean") return typeof raw === "boolean" ? raw : def;
  if (Array.isArray(def)) {
    if (!Array.isArray(raw)) return def;
    const template = def[0];
    if (template === undefined) return raw;
    if (typeof template === "string") {
      return raw.filter((x) => typeof x === "string" && x.trim()).slice(0, 40);
    }
    return raw
      .filter((x) => x && typeof x === "object" && !Array.isArray(x))
      .map((x) => overlay(template, x))
      .slice(0, 40);
  }
  if (def && typeof def === "object") {
    const src = raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(def)) out[k] = overlay(def[k], (src as any)[k]);
    return out;
  }
  return raw ?? def;
}

export function normalizeLpColdDripContent(raw: unknown): LpColdDripContent {
  return overlay(LP_COLD_DRIP_DEFAULT_CONTENT, raw) as LpColdDripContent;
}

// True when the blob carries enough structure to render the offer page.
export function isLpColdDripContentUsable(
  cd: LpColdDripContent | null | undefined,
): boolean {
  return Boolean(cd && cd.hero?.headline && cd.buyBox?.title);
}

// ── Regen block keys ─────────────────────────────────────────────────────────
// Per-section copy regen uses dynamic block keys "cold-drip-<sectionKey>"
// (resolved by lpCopyBlockLabel in shared/lp-express-sections.ts), mirroring
// the advertorial's "advertorial-section-<n>" precedent.

export function lpColdDripRegenKey(sectionKey: string): string {
  return `cold-drip-${sectionKey}`;
}

export const LP_COLD_DRIP_REGEN_KEY_RE = /^cold-drip-([A-Za-z]+)$/;

// ── AI image slots ───────────────────────────────────────────────────────────
// Lifestyle/editorial imagery generated per-slot through the standard LP
// Express single-slot pipeline (SINGLE_SLOT_IMAGE_CONFIGS "coldDripImages" in
// lp-express-worker.ts) and stored on generatedContent.coldDripImages. The
// logo, buy-box bundle photo, and per-product duo cards stay REAL brand/
// product assets — only the ingredient close-ups, the community wall, and the
// two brand-story photos are AI-generated. Slot keys are matched by the
// template component's MediaSlot call sites:
//   ingredient-<i> → ingredients.cards[i] photo (up to 4 cards — the copy
//                    workflow returns a VARIABLE number of ingredient cards,
//                    so slots are seeded per-page via coldDripImageSlotsFor)
//   community-<i>  → gaffleWorld.tiktoks[i] tile (8 tiles, 9:13 portrait)
//   story/skincare → whoWeAre.behindTheDrip / whoWeAre.skincare card photos
// Each slot carries the Fal aspect_ratio matching its on-page container so
// renders fill their frame instead of being cover-cropped from a square:
// ingredient/story strips are wide (16:9), community tiles are portrait (2:3,
// the closest supported ratio to the 9:13 tile).
export const LP_COLD_DRIP_IMAGE_SLOTS: {
  key: string;
  variantHint: string;
  aspectRatio?: string;
}[] = [
  {
    key: "ingredient-0",
    aspectRatio: "16:9",
    variantHint:
      "Ingredient macro shot #1: the product's FIRST key ingredient or component up close (raw material, texture, or source), isolated on a clean warm background; editorial food/product photography, wide landscape composition filling the entire frame edge-to-edge, no borders or white margins, no text overlays.",
  },
  {
    key: "ingredient-1",
    aspectRatio: "16:9",
    variantHint:
      "Ingredient macro shot #2: the product's SECOND key ingredient or component up close (a different material than shot #1), isolated on a clean warm background; editorial food/product photography, wide landscape composition filling the entire frame edge-to-edge, no borders or white margins, no text overlays.",
  },
  {
    key: "ingredient-2",
    aspectRatio: "16:9",
    variantHint:
      "Ingredient macro shot #3: the product's THIRD key ingredient or component up close (a clearly different material than shots #1 and #2), isolated on a clean warm background; editorial food/product photography, wide landscape composition filling the entire frame edge-to-edge, no borders or white margins, no text overlays.",
  },
  {
    key: "ingredient-3",
    aspectRatio: "16:9",
    variantHint:
      "Ingredient macro shot #4: the product's FOURTH key ingredient or component up close (a clearly different material than shots #1–#3), isolated on a clean warm background; editorial food/product photography, wide landscape composition filling the entire frame edge-to-edge, no borders or white margins, no text overlays.",
  },
  {
    key: "community-0",
    aspectRatio: "2:3",
    variantHint:
      "Community/UGC-style photo #1: a real-looking customer enjoying the product in a casual everyday moment, person and product both clearly visible; candid smartphone-photo energy, natural light, full-bleed vertical portrait composition filling the entire frame edge-to-edge, no borders or white margins, no text overlays.",
  },
  {
    key: "community-1",
    aspectRatio: "2:3",
    variantHint:
      "Community/UGC-style photo #2: the product held up close to camera by a hand in an authentic indoor setting (kitchen counter, car cupholder, or desk — NOT a landscape); candid smartphone-photo energy, full-bleed vertical portrait composition filling the entire frame edge-to-edge, no borders or white margins, no text overlays.",
  },
  {
    key: "community-2",
    aspectRatio: "2:3",
    variantHint:
      "Community/UGC-style photo #3: the product being made, poured, or prepared — a close process moment with hands in frame; candid, natural light, full-bleed vertical portrait composition filling the entire frame edge-to-edge, no borders or white margins, no text overlays.",
  },
  {
    key: "community-3",
    aspectRatio: "2:3",
    variantHint:
      "Community/UGC-style photo #4: a customer on the go with the product in an URBAN or indoor setting (city sidewalk, commute, gym bag — NOT a farm or field); candid smartphone-photo energy, full-bleed vertical portrait composition filling the entire frame edge-to-edge, no borders or white margins, no text overlays.",
  },
  {
    key: "community-4",
    aspectRatio: "2:3",
    variantHint:
      "Community/UGC-style photo #5: a golden-hour moment with a person holding the product on a patio, balcony, or porch — person and product in the foreground; warm candid tones, full-bleed vertical portrait composition filling the entire frame edge-to-edge, no borders or white margins, no text overlays.",
  },
  {
    key: "community-5",
    aspectRatio: "2:3",
    variantHint:
      "Community/UGC-style photo #6: two or three friends sharing the product together indoors — social, joyful, authentic faces; candid smartphone-photo energy, full-bleed vertical portrait composition filling the entire frame edge-to-edge, no borders or white margins, no text overlays.",
  },
  {
    key: "community-6",
    aspectRatio: "2:3",
    variantHint:
      "Community/UGC-style photo #7: a stylish flat-lay or tabletop scene featuring the product with everyday props; candid, natural light, full-bleed vertical portrait composition filling the entire frame edge-to-edge, no borders or white margins, no text overlays.",
  },
  {
    key: "community-7",
    aspectRatio: "2:3",
    variantHint:
      "Community/UGC-style photo #8: a festive or seasonal indoor moment with the product (holiday decor, celebration), clearly distinct from the other community photos; candid, warm, full-bleed vertical portrait composition filling the entire frame edge-to-edge, no borders or white margins, no text overlays.",
  },
  {
    key: "story",
    aspectRatio: "16:9",
    variantHint:
      "Brand-story photo #1 (documentary): behind the scenes of how the product is made or where the brand began — founders, workspace, or craft process; warm authentic documentary style, wide landscape composition filling the entire frame edge-to-edge, no borders or white margins, no text overlays.",
  },
  {
    key: "skincare",
    aspectRatio: "16:9",
    variantHint:
      "Brand-story photo #2 (documentary): a complementary scene from the brand's world highlighting a secondary benefit or ritual of the product in daily life; warm authentic documentary style, distinct from photo #1, wide landscape composition filling the entire frame edge-to-edge, no borders or white margins, no text overlays.",
  },
];

// The copy workflow returns a VARIABLE number of ingredient cards (the
// skeleton has 2 but real runs come back with up to 4), and the community
// wall/tiktok tile count can in principle vary too. Seed (and background-fill)
// only the slots the page's copy blob actually renders so we never pay for
// renders with no on-page home — and every rendered card gets a slot.
export function coldDripImageSlotsFor(content: {
  ingredients?: { cards?: unknown[] };
  gaffleWorld?: { tiktoks?: unknown[] };
}): { key: string; variantHint: string; aspectRatio?: string }[] {
  const cards = Array.isArray(content?.ingredients?.cards)
    ? (content.ingredients.cards as { title?: string; lead?: string }[])
    : [];
  const ingredientCount = Math.min(cards.length, 4);
  const tileCount = Math.min(
    Array.isArray(content?.gaffleWorld?.tiktoks) ? content.gaffleWorld.tiktoks.length : 0,
    8,
  );
  return LP_COLD_DRIP_IMAGE_SLOTS.filter((s) => {
    const ing = /^ingredient-(\d+)$/.exec(s.key);
    if (ing) return Number(ing[1]) < ingredientCount;
    const com = /^community-(\d+)$/.exec(s.key);
    if (com) return Number(com[1]) < tileCount;
    return true;
  }).map((s) => {
    // Ground each ingredient card's image in the card's ACTUAL copy. The
    // static hints only say "the product's Nth key ingredient", so the prompt
    // workflow had nothing to differentiate the cards with and fell back to
    // the same generic brand-vibe scene for every one (four near-identical
    // pasture shots). Naming the real subject per slot forces distinct images.
    const ing = /^ingredient-(\d+)$/.exec(s.key);
    if (!ing) return s;
    const card = cards[Number(ing[1])];
    const title = typeof card?.title === "string" ? card.title.trim() : "";
    if (!title) return s;
    const lead = typeof card?.lead === "string" ? card.lead.trim() : "";
    return {
      ...s,
      variantHint:
        `Ingredient macro shot #${Number(ing[1]) + 1}: the subject of THIS card is "${title}"` +
        (lead ? ` (${lead})` : "") +
        `. Show that exact ingredient/material up close — its raw form, texture, or source — so the photo is unmistakably about ${title} and clearly different from the page's other ingredient photos. NOT a generic farm, field, or landscape scene. Isolated on a clean warm background; editorial food/product photography, wide landscape composition filling the entire frame edge-to-edge, no borders or white margins, no text overlays.`,
    };
  });
}

// ── Brand-facts sanitizer ────────────────────────────────────────────────────
// The default blob is the literal Gaffle export, and the copy workflow
// rewrites PROSE but has no data for FACTS (checkout URL, prices, address,
// phone, email, social handles, footer links) — so Gaffle facts used to leak
// verbatim into generated pages for other brands. This deterministic pass runs
// server-side on EVERY generated blob (Sim, grok-revision, and the sync
// default path) and:
//   • overwrites `pricing` from the real product (pdpUrl + priceText, with the
//     template's discount % — or the attached sale's %) — pricing is config,
//     not copy, so the workflow's output is always ignored here;
//   • blanks location/contact facts we have no brand data for (address, maps
//     URL, phone, email) instead of shipping Gaffle's real ones — the fields
//     stay editable in the builder;
//   • points every gaffle.coffee URL/handle at the brand's own site/socials;
//   • replaces any remaining "Gaffle" token in copy with the brand name.
// A page generated FOR the Gaffle brand itself is left untouched.

export interface ColdDripBrandFacts {
  brandName: string;
  brandUrl?: string | null;
  socialProfiles?: unknown;
  productPdpUrl?: string | null;
  productPriceText?: string | null;
  /** Percent off for the offer pricing pair (attached sale's %, default 20). */
  discountPercent?: number | null;
}

function coldDripNormalizeUrl(raw: unknown): string {
  const s = typeof raw === "string" ? raw.trim() : "";
  if (!s) return "";
  return /^https?:\/\//i.test(s) ? s : `https://${s}`;
}

function coldDripSocialUrl(profiles: unknown, platform: string): string {
  if (!Array.isArray(profiles)) return "";
  for (const p of profiles) {
    const plat = String((p as any)?.platform ?? "").toLowerCase();
    const url = coldDripNormalizeUrl((p as any)?.url);
    if (url && plat.includes(platform)) return url;
  }
  return "";
}

function coldDripHandleFromUrl(url: string): string {
  const m = /\/@?([A-Za-z0-9._-]+)\/?(?:[?#].*)?$/.exec(url);
  return m ? m[1] : "";
}

function coldDripMapStrings(node: unknown, fn: (s: string) => string): unknown {
  if (typeof node === "string") return fn(node);
  if (Array.isArray(node)) return node.map((x) => coldDripMapStrings(x, fn));
  if (node && typeof node === "object") {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(node as Record<string, unknown>)) {
      out[k] = coldDripMapStrings((node as Record<string, unknown>)[k], fn);
    }
    return out;
  }
  return node;
}

// Builds the facts bag from raw brand/product/sale rows (loose typing so both
// the worker's assembled rows and route-side storage rows can be passed).
export function coldDripBrandFactsFromRows(
  brand: any,
  product: any,
  sale?: any,
): ColdDripBrandFacts {
  let discountPercent: number | null = null;
  if (sale && String(sale.saleType || "") === "percentage_off") {
    const m = /(\d+(?:\.\d+)?)/.exec(String(sale.discountValue || ""));
    if (m) discountPercent = Number(m[1]);
  }
  return {
    brandName: String(brand?.name || ""),
    brandUrl: brand?.url ?? null,
    socialProfiles: brand?.socialProfiles,
    productPdpUrl: product?.pdpUrl ?? null,
    productPriceText: product?.priceText ?? null,
    discountPercent,
  };
}

export function applyColdDripBrandFacts(
  content: LpColdDripContent,
  facts: ColdDripBrandFacts,
): LpColdDripContent {
  const brandName = (facts.brandName || "").trim();
  // The reference brand's own pages keep their real facts.
  if (!brandName || /gaffle/i.test(brandName)) return content;

  const siteUrl = coldDripNormalizeUrl(facts.brandUrl);
  const siteHost = siteUrl ? siteUrl.replace(/^https?:\/\/(www\.)?/i, "").replace(/\/.*$/, "") : "";
  const tiktokUrl = coldDripSocialUrl(facts.socialProfiles, "tiktok");
  const instaUrl = coldDripSocialUrl(facts.socialProfiles, "instagram");
  const socialUrl = tiktokUrl || instaUrl;
  const handle = coldDripHandleFromUrl(socialUrl);
  const handleText = handle ? `@${handle}` : brandName;

  // ── Structured facts first (so the generic scrub never sees them) ──
  // Pricing is config, not copy: always derived, workflow output ignored.
  const pdpUrl = coldDripNormalizeUrl(facts.productPdpUrl);
  content.pricing.checkoutUrl = pdpUrl || siteUrl || "";
  const priceMatch = /(\d+(?:\.\d+)?)/.exec(String(facts.productPriceText ?? ""));
  const fullPrice = priceMatch ? Number(priceMatch[1]) : NaN;
  if (Number.isFinite(fullPrice) && fullPrice > 0) {
    const rawPct = Number(facts.discountPercent);
    const pct = Number.isFinite(rawPct) && rawPct > 0 && rawPct < 95 ? rawPct : 20;
    content.pricing.bundleFullPrice = fullPrice;
    content.pricing.bundlePriceOneTime =
      Math.round(fullPrice * (1 - pct / 100) * 100) / 100;
  }
  // Location/contact facts we have no brand data for: blank, never false.
  if (/temecula/i.test(content.visit.badge)) content.visit.badge = "VISIT US";
  // The door sign is Gaffle's coffee-bar concept ("Gaffle Pour Bar" /
  // "Pouring Now") — a token swap would produce "<Brand> Pour Bar" for brands
  // that aren't coffee bars, so use the plain brand name + a neutral banner.
  if (/pour bar|gaffle/i.test(content.visit.doorSign.header)) {
    content.visit.doorSign.header = brandName;
  }
  if (/pour/i.test(content.visit.doorSign.openBanner)) {
    content.visit.doorSign.openBanner = "Open Now";
  }
  if (/pour/i.test(content.visit.doorSign.addressCallout)) {
    content.visit.doorSign.addressCallout = "Come say hi in person.";
  }
  content.visit.doorSign.address = "";
  content.visit.doorSign.mapsUrl = "";
  // With no maps URL the CTA is a dead link — blank it (renderer hides empty).
  content.visit.doorSign.mapsCta = "";
  content.visit.contacts.phone = "";
  content.visit.contacts.phoneHref = "";
  content.visit.contacts.email = "";
  content.visit.contacts.emailHref = "";
  // The labels ("Call ahead" / "Wholesale") are Gaffle-specific and dangle
  // without their values — blank them too (still editable in the builder).
  content.visit.contacts.phoneLabel = "";
  content.visit.contacts.emailLabel = "";
  // Community follow CTA points at the brand's own social profile.
  content.gaffleWorld.followUrl = socialUrl || siteUrl || "";
  if (/gaffle/i.test(content.gaffleWorld.followCta) || !content.gaffleWorld.followCta.trim()) {
    content.gaffleWorld.followCta = tiktokUrl
      ? `Follow ${handleText} on TikTok`
      : instaUrl
        ? `Follow ${handleText} on Instagram`
        : `Follow ${brandName}`;
  }
  if (/gaffle/i.test(content.footer.copyright)) {
    content.footer.copyright = `© {year} ${brandName}. All rights reserved.`;
  }

  // ── Generic token scrub across every remaining string ──
  const scrub = (input: string): string => {
    let s = input;
    s = s.replace(/https?:\/\/(?:www\.)?gaffle\.coffee[^\s"'<>)]*/gi, siteUrl || "");
    s = s.replace(/[\w.+-]+@gaffle\.coffee/gi, "");
    s = s.replace(/@gaffle\.coffee/gi, handleText);
    s = s.replace(/\bgaffle\.coffee\b/gi, siteHost || brandName);
    s = s.replace(/\bgaffle\b/gi, brandName);
    // Gaffle's home town is a fact, not copy — drop it from prose.
    s = s.replace(/\s*(?:in\s+)?Temecula(?:\s*,\s*(?:CA(?:lifornia)?\.?))?/gi, "");
    // Tidy leftover separators from removed fragments.
    s = s.replace(/\s{2,}/g, " ").replace(/\s+([,.])/g, "$1");
    s = s.replace(/[·,]\s*$/g, "").trim();
    return s;
  };
  return coldDripMapStrings(content, scrub) as LpColdDripContent;
}
